import { pluralize } from '../string';

// Files larger than 25MB use multipart upload with full progress tracking (25/50/75/100%)
// Files smaller use simple PUT with completion notification only
const MAX_FILE_SIZE = 25 * 1000 * 1000;

export function isVideoMimeType(type: string) {
  return type.match('video.*');
}
export function isVideoFile(file: File) {
  return isVideoMimeType(file.type);
}
export function isVideoItem(item: DataTransferItem) {
  return item.kind === 'file' && isVideoMimeType(item.type);
}


export function isAudioMimeType(type: string) {
  return type.match('audio.*');
}
export function isAudioFile(file: File) {
  return isAudioMimeType(file.type);
}
export function isAudioItem(item: DataTransferItem) {
  return item.kind === 'file' && isAudioMimeType(item.type);
}


export function isMediaFile(file: File) {
  return (
    isVideoFile(file) ||
    isAudioFile(file));
}
export function isMediaItem(item: DataTransferItem) {
  return (
    isVideoItem(item) ||
    isAudioItem(item));
}

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/html',
  'application/x-xliff+xml',
  'application/xliff+xml',
];

const DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.txt', '.html', '.htm', '.xlf', '.xliff'];

export const SUPPORTED_DOCUMENT_TYPES_LABEL = 'PDF, DOCX, TXT, HTML';

export function isDocumentMimeType(type: string) {
  return DOCUMENT_MIME_TYPES.includes(type);
}
export function isDocumentFile(file: File) {
  if (isDocumentMimeType(file.type)) return true;
  // Fallback to extension check for MIME types that may not be detected
  const ext = file.name.toLowerCase().split('.').pop();
  return ext ? DOCUMENT_EXTENSIONS.includes(`.${ext}`) : false;
}
export function isDocumentItem(item: DataTransferItem) {
  return item.kind === 'file' && isDocumentMimeType(item.type);
}

// Drag feedback for the document dropzone. A drag carrying no file items is almost always
// content dragged from an email or another app (text/links), which the browser cannot expose
// as an uploadable file — guide the user instead of showing a generic "not a document" error.
export function documentDragErrorMessage(items: DataTransferItem[]): string | null {
  if (items.length === 0) return null;
  const fileItems = items.filter(item => item.kind === 'file');
  if (fileItems.length === 0)
    return 'That looks like content dragged from an email or another app, not a saved file. '
      + 'Download the file first and drag it in — or use the Google Drive button below.';
  const nonDocumentCount = fileItems.filter(item => !isDocumentItem(item)).length;
  if (nonDocumentCount > 0)
    return pluralize(
      nonDocumentCount,
      `file is not a supported document (${SUPPORTED_DOCUMENT_TYPES_LABEL})`,
      `files are not supported documents (${SUPPORTED_DOCUMENT_TYPES_LABEL})`);
  return null;
}


export function isJobPosterCompatible(file: DataTransferItem) {
  return file.kind === 'file' && ['image/jpeg', 'image/png'].includes(file.type);
}

export function isLargeFile(file: File) {
  return file.size > MAX_FILE_SIZE;
}